import { createApi } from '@reduxjs/toolkit/query/react';
import { customHandoverBaseQuery } from '../customBaseQuery';
import {
  AiSuggestResponse,
  DepartureCapsule,
  DepartureCapsuleFile,
  DepartureQuestion,
  DepartureQuestionnaire,
  DepartureRelatedHandover,
  PublishDepartureRequest,
  PublishDepartureRecommendationResponse,
  PublishDepartureResponse,
  StepId,
} from '../../features/departure/types';


export const departureApi = createApi({
  reducerPath: 'departureApi',
  baseQuery: customHandoverBaseQuery,
  tagTypes: ['Departure', 'DepartureList'],
  refetchOnMountOrArgChange: 30,
  refetchOnReconnect: true,
  endpoints: builder => ({
    triggerDeparture: builder.mutation<
      { message: string; departureId: string },
      {
        departingEmployeeEmail: string;
        departureDate?: string;
        incomingEmployeeEmail?: string;
        questions?: DepartureQuestion[];
      }
    >({
      query: body => ({ url: '/departure', method: 'post', body }),
      invalidatesTags: ['DepartureList'],
    }),

    getMyDeparture: builder.query<DepartureQuestionnaire | null, void>({
      query: () => ({ url: '/departure/my', method: 'get' }),
      transformResponse: (res: { departure: DepartureQuestionnaire }) => res.departure ?? null,
      providesTags: ['Departure'],
    }),

    getAllDepartures: builder.query<DepartureQuestionnaire[], void>({
      query: () => ({ url: '/departure/all', method: 'get' }),
      transformResponse: (res: { departures: DepartureQuestionnaire[] }) => res.departures ?? [],
      providesTags: ['DepartureList'],
    }),

    getDepartureById: builder.query<DepartureQuestionnaire, string>({
      query: id => ({ url: `/departure/${id}`, method: 'get' }),
      transformResponse: (res: { departure: DepartureQuestionnaire }) => res.departure,
      providesTags: (_, __, id) => [{ type: 'Departure', id }],
    }),

    saveStep: builder.mutation<
      { message: string },
      {
        departureId: string;
        stepId: StepId;
        items: any[];
        completed: boolean;
        relatedHandover?: DepartureRelatedHandover;
      }
    >({
      query: ({ departureId, ...body }) => ({
        url: `/departure/${departureId}/step`,
        method: 'put',
        body,
      }),
      invalidatesTags: ['Departure', 'DepartureList'],
    }),

    completeDeparture: builder.mutation<
      { message: string },
      string
    >({
      query: departureId => ({ url: `/departure/${departureId}/complete`, method: 'put' }),
      invalidatesTags: ['Departure', 'DepartureList'],
    }),

    getDepartureSuggestions: builder.mutation<
      AiSuggestResponse,
      { departureId: string; stepId: StepId }
    >({
      query: body => ({ url: '/ai/departure-suggest', method: 'post', body }),
    }),

    uploadCapsuleFile: builder.mutation<
      { message: string; capsule: DepartureCapsule; capsuleFile: DepartureCapsuleFile },
      { departureId: string; file: string; filename: string; mimeType: string; size: number }
    >({
      query: ({ departureId, ...body }) => ({
        url: `/departure/${departureId}/capsule/file`,
        method: 'post',
        body,
      }),
      invalidatesTags: ['Departure', 'DepartureList'],
    }),

    deleteCapsuleFile: builder.mutation<
      { message: string; capsule: DepartureCapsule },
      { departureId: string; fileId: string }
    >({
      query: ({ departureId, fileId }) => ({
        url: `/departure/${departureId}/capsule/file/${fileId}`,
        method: 'delete',
      }),
      invalidatesTags: ['Departure', 'DepartureList'],
    }),

    sealCapsule: builder.mutation<
      { message: string; capsule: DepartureCapsule },
      string
    >({
      query: departureId => ({
        url: `/departure/${departureId}/capsule/seal`,
        method: 'put',
      }),
      invalidatesTags: ['Departure', 'DepartureList'],
    }),

    reopenCapsule: builder.mutation<
      { message: string; capsule: DepartureCapsule },
      string
    >({
      query: departureId => ({
        url: `/departure/${departureId}/capsule/reopen`,
        method: 'put',
      }),
      invalidatesTags: ['Departure', 'DepartureList'],
    }),

    generateCapsuleDraft: builder.mutation<
      { message: string; departure: DepartureQuestionnaire; summary: string },
      string
    >({
      query: departureId => ({
        url: `/departure/${departureId}/capsule/generate`,
        method: 'post',
      }),
      invalidatesTags: ['Departure', 'DepartureList'],
    }),

    generateHandoverDraft: builder.mutation<
      { message: string; departure: DepartureQuestionnaire },
      string
    >({
      query: departureId => ({
        url: `/departure/${departureId}/handover-draft`,
        method: 'post',
      }),
      invalidatesTags: ['Departure', 'DepartureList'],
    }),
    getPublishRecommendation: builder.mutation<
      PublishDepartureRecommendationResponse,
      { departureId: string }
    >({
      query: ({ departureId }) => ({
        url: `/departure/${departureId}/publish-recommendation`,
        method: 'post',
      }),
    }),

    assignIncomingEmployee: builder.mutation<
      { message: string; departure?: DepartureQuestionnaire },
      { departureId: string; incomingEmployeeEmail: string }
    >({
      query: ({ departureId, incomingEmployeeEmail }) => ({
        url: `/departure/${departureId}/incoming`,
        method: 'put',
        body: { incomingEmployeeEmail },
      }),
      invalidatesTags: (_result, _error, { departureId }) => [
        'DepartureList',
        { type: 'Departure', id: departureId },
      ],
    }),

    publishDeparture: builder.mutation<
      PublishDepartureResponse,
      { departureId: string; data: PublishDepartureRequest }
    >({
      query: ({ departureId, data }) => ({
        url: `/departure/${departureId}/publish`,
        method: 'post',
        body: data,
      }),
      invalidatesTags: (result, _error, { departureId }) => [
        'DepartureList',
        { type: 'Departure', id: departureId },
      ],
    }),

    cancelDeparture: builder.mutation<
      { message: string; departure: DepartureQuestionnaire },
      string
    >({
      query: departureId => ({
        url: `/departure/${departureId}/cancel`,
        method: 'post',
      }),
      invalidatesTags: (result, _error, departureId) => [
        'DepartureList',
        { type: 'Departure', id: departureId },
      ],
    }),
    saveCustomAnswer: builder.mutation<
      { message: string },
      { departureId: string; questionId: string; answer: string; completed: boolean }
    >({
      query: ({ departureId, ...body }) => ({
        url: `/departure/${departureId}/custom-answer`,
        method: 'put',
        body,
      }),
      invalidatesTags: ['Departure', 'DepartureList'],
    }),

    uploadExitInterview: builder.mutation<
      { message: string; departure: DepartureQuestionnaire },
      { departureId: string; file: string; filename: string; mimeType: string; size: number }
    >({
      query: ({ departureId, ...body }) => ({
        url: `/departure/${departureId}/exit-interview`,
        method: 'post',
        body,
      }),
      invalidatesTags: ['Departure', 'DepartureList'],
    }),

    getExitInterviewUrl: builder.query<{ url: string }, string>({
      query: departureId => ({ url: `/departure/${departureId}/exit-interview/url`, method: 'get' }),
    }),

    transcribeExitInterview: builder.mutation<
      { message: string; departure: DepartureQuestionnaire },
      string
    >({
      query: departureId => ({
        url: `/departure/${departureId}/exit-interview/transcribe`,
        method: 'post',
      }),
      invalidatesTags: ['Departure', 'DepartureList'],
    }),

    deleteExitInterview: builder.mutation<
      { message: string; departure: DepartureQuestionnaire },
      string
    >({
      query: departureId => ({
        url: `/departure/${departureId}/exit-interview`,
        method: 'delete',
      }),
      invalidatesTags: ['Departure', 'DepartureList'],
    }),
    setExitInterviewOptional: builder.mutation<
      { message: string; departure: DepartureQuestionnaire },
      { departureId: string; notRequired: boolean }
    >({
      query: ({ departureId, notRequired }) => ({
        url: `/departure/${departureId}/exit-interview/optional`,
        method: 'put',
        body: { notRequired },
      }),
      invalidatesTags: ['Departure', 'DepartureList'],
    }),
    saveManagerNote: builder.mutation<
      { message: string; departure: DepartureQuestionnaire },
      { departureId: string; sectionId: string; note: string }
    >({
      query: ({ departureId, sectionId, note }) => ({
        url: `/departure/${departureId}/manager-note`,
        method: 'put',
        body: { sectionId, note },
      }),
      invalidatesTags: ['Departure', 'DepartureList'],
    }),
  }),
});

export const {
  useTriggerDepartureMutation,
  useGetMyDepartureQuery,
  useGetAllDeparturesQuery,
  useGetDepartureByIdQuery,
  useSaveStepMutation,
  useCompleteDepartureMutation,
  useGetDepartureSuggestionsMutation,
  useUploadCapsuleFileMutation,
  useDeleteCapsuleFileMutation,
  useSealCapsuleMutation,
  useReopenCapsuleMutation,
  useGenerateCapsuleDraftMutation,
  useGenerateHandoverDraftMutation,
  useGetPublishRecommendationMutation,
  useAssignIncomingEmployeeMutation,
  usePublishDepartureMutation,
  useCancelDepartureMutation,
  useSaveCustomAnswerMutation,
  useUploadExitInterviewMutation,
  useGetExitInterviewUrlQuery,
  useTranscribeExitInterviewMutation,
  useDeleteExitInterviewMutation,
  useSetExitInterviewOptionalMutation,
  useSaveManagerNoteMutation,
} = departureApi;
